import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Navigation from './Navigation';
import { api } from '../services/api';
import './CSVUpload.css';

interface ParsedTransaction {
  transactionDate: string;
  amount: number;
  category: string;
  transaction_name: string;
  type: 'expense' | 'income';
}

interface Account {
  accountId: number;
  accountName: string;
  isDefault: boolean;
}

const CSVUpload: React.FC = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const [file, setFile] = useState<File | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [preview, setPreview] = useState<ParsedTransaction[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadResults, setUploadResults] = useState<{ success: number; failed: number; errors: string[] } | null>(null);

  const fetchAccounts = useCallback(async () => {
    if (!userId) return;
    
    try {
      const response = await api.get(`/accounts/user/${userId}/active`);
      if (!response.ok) throw new Error('Failed to fetch accounts');
      const data = await response.json();
      setAccounts(data);
      // Set default account if available
      const defaultAccount = data.find((acc: Account) => acc.isDefault);
      if (defaultAccount) {
        setSelectedAccountId(defaultAccount.accountId.toString());
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      navigate('/');
      return;
    }
    fetchAccounts();
  }, [userId, navigate, fetchAccounts]);

  const parseExcel = (file: File): Promise<ParsedTransaction[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get the first sheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
          
          if (jsonData.length < 2) {
            throw new Error('Excel file must have at least a header row and one data row');
          }

          // Parse header (first row)
          const header = jsonData[0].map((h: any) => String(h || '').trim().toLowerCase());
          
          // Find column indices
          const nameIndex = header.findIndex(h => h === 'name' || h === 'transaction_name' || h === 'transactionname');
          const dateIndex = header.findIndex(h => h === 'transactiondate' || h === 'transaction_date' || h === 'date');
          const amountIndex = header.findIndex(h => h === 'amount');
          const categoryIndex = header.findIndex(h => h === 'category');
          const typeIndex = header.findIndex(h => h === 'type');

          if (nameIndex === -1 || dateIndex === -1 || amountIndex === -1 || categoryIndex === -1 || typeIndex === -1) {
            throw new Error('Excel must contain columns: name, transactionDate, amount, category, type');
          }

          const transactions: ParsedTransaction[] = [];

          // Parse data rows (skip header row)
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            
            if (!row || row.length === 0) {
              continue; // Skip empty rows
            }

            const name = String(row[nameIndex] || '').trim();
            const transactionDate = row[dateIndex] ? String(row[dateIndex]).trim() : '';
            const amountValue = row[amountIndex];
            const category = String(row[categoryIndex] || 'Other').trim();
            const typeValue = String(row[typeIndex] || '').trim().toLowerCase();

            // Validate and parse amount
            let amount: number;
            if (typeof amountValue === 'number') {
              amount = amountValue;
            } else {
              amount = parseFloat(String(amountValue || '0'));
            }
            
            if (isNaN(amount) || amount === 0) {
              continue; // Skip invalid amounts
            }

            // Determine transaction type
            let type: 'expense' | 'income';
            if (typeValue === 'income' || typeValue === 'expense') {
              type = typeValue as 'expense' | 'income';
            } else {
              // Fallback: positive = income, negative = expense
              type = amount < 0 ? 'expense' : 'income';
            }
            
            const absoluteAmount = Math.abs(amount);

            // Use name from Excel, fallback to category if empty
            const transaction_name = name || category || 'Transaction';

            // Validate and format date
            let formattedDate = transactionDate;
            if (transactionDate || typeof row[dateIndex] === 'number') {
              // Handle Excel date serial numbers
              if (typeof row[dateIndex] === 'number') {
                // Excel date serial number (days since 1900-01-01)
                // Excel epoch is 1900-01-01, but Excel incorrectly treats 1900 as a leap year
                // So we use Dec 30, 1899 as the base
                const excelSerial = row[dateIndex];
                const excelEpoch = new Date(1899, 11, 30); // Dec 30, 1899
                const jsDate = new Date(excelEpoch.getTime() + excelSerial * 86400000);
                if (!isNaN(jsDate.getTime())) {
                  formattedDate = jsDate.toISOString().split('T')[0];
                } else {
                  formattedDate = new Date().toISOString().split('T')[0];
                }
              } else if (transactionDate) {
                // Try to parse as date string
                const date = new Date(transactionDate);
                if (!isNaN(date.getTime())) {
                  formattedDate = date.toISOString().split('T')[0];
                } else {
                  // Try common date formats
                  const dateMatch = transactionDate.match(/(\d{4})[-/](\d{2})[-/](\d{2})/);
                  if (dateMatch) {
                    formattedDate = `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
                  } else {
                    formattedDate = new Date().toISOString().split('T')[0]; // Default to today
                  }
                }
              } else {
                formattedDate = new Date().toISOString().split('T')[0]; // Default to today
              }
            } else {
              formattedDate = new Date().toISOString().split('T')[0]; // Default to today
            }

            transactions.push({
              transactionDate: formattedDate,
              amount: absoluteAmount,
              category,
              transaction_name,
              type
            });
          }

          resolve(transactions);
        } catch (error: any) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
    if (fileExtension !== 'xlsx' && fileExtension !== 'xls') {
      setError('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setFile(selectedFile);
    setError('');
    setSuccess('');
    setUploadResults(null);
    setPreview([]);

    try {
      const parsed = await parseExcel(selectedFile);
      setPreview(parsed);
    } catch (err: any) {
      setError(err.message || 'Failed to parse Excel file');
      setPreview([]);
    }
  };

  const handleUpload = async () => {
    if (!userId || preview.length === 0) {
      setError('No transactions to upload');
      return;
    }

    if (!selectedAccountId) {
      setError('Please select an account');
      return;
    }

    setIsUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress({ current: 0, total: preview.length });

    let successCount = 0;
    let failedCount = 0;
    const errors: string[] = [];

    for (let i = 0; i < preview.length; i++) {
      const transaction = preview[i];
      
      try {
        const response = await api.post(`/transactions`, {
          transaction_name: transaction.transaction_name,
          amount: transaction.amount,
          type: transaction.type,
          category: transaction.category,
          transaction_date: transaction.transactionDate,
          account: {
            accountId: parseInt(selectedAccountId)
          },
          user: {
            userId: parseInt(userId)
          }
        });

        if (response.ok) {
          successCount++;
        } else {
          failedCount++;
          const errorData = await response.json().catch(() => ({ errorMessage: 'Unknown error' }));
          errors.push(`Row ${i + 2}: ${errorData.errorMessage || 'Failed to create transaction'}`);
        }
      } catch (err: any) {
        failedCount++;
        errors.push(`Row ${i + 2}: ${err.message || 'Network error'}`);
      }

      setUploadProgress({ current: i + 1, total: preview.length });
    }

    setIsUploading(false);
    setUploadResults({ success: successCount, failed: failedCount, errors });

    if (successCount > 0) {
      setSuccess(`Successfully uploaded ${successCount} transaction(s)`);
    }
    if (failedCount > 0) {
      setError(`Failed to upload ${failedCount} transaction(s). Check details below.`);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KW', {
      style: 'decimal',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount) + ' KD';
  };

  return (
    <div className="csv-upload-container">
      <Navigation />
      <div className="csv-upload-content">
        <h1 className="csv-upload-title">Upload Transactions from Excel</h1>

        <div className="csv-upload-instructions">
          <h3>Instructions:</h3>
          <ul>
            <li>Excel file must contain the following columns: <strong>name, transactionDate, amount, category, type</strong></li>
            <li>Name: Transaction name/description</li>
            <li>Date format: YYYY-MM-DD (e.g., 2025-01-15) or Excel date format</li>
            <li>Amount: Numeric value (positive or negative)</li>
            <li>Type: Must be either "income" or "expense" (case-insensitive)</li>
            <li>Category: Use existing categories (Food, Transportation, Shopping, etc.)</li>
            <li>First row should be the header row</li>
          </ul>
        </div>

        <div className="file-upload-section">
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="upload-account" className="form-label" style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px', display: 'block' }}>
              Select Account *
            </label>
            <select
              id="upload-account"
              className="form-input"
              style={{ width: '100%', padding: '14px 16px', fontSize: '16px', border: '2px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#f7fafc', color: '#1a202c' }}
              value={selectedAccountId}
              onChange={(e) => {
                setSelectedAccountId(e.target.value);
                setError('');
              }}
              required
            >
              <option value="">Select an account</option>
              {accounts.map((account) => (
                <option key={account.accountId} value={account.accountId}>
                  {account.accountName} {account.isDefault ? '(Default)' : ''}
                </option>
              ))}
            </select>
          </div>

          <label htmlFor="excel-file" className="file-upload-label">
            <span className="upload-icon">📁</span>
            Choose Excel File
            <input
              type="file"
              id="excel-file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="file-input"
            />
          </label>
          {file && (
            <div className="file-info">
              <span className="file-name">{file.name}</span>
              <span className="file-size">({(file.size / 1024).toFixed(2)} KB)</span>
            </div>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {preview.length > 0 && (
          <div className="preview-section">
            <h3>Preview ({preview.length} transactions)</h3>
            <div className="preview-table-container">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.slice(0, 10).map((transaction, index) => (
                    <tr key={index}>
                      <td>{transaction.transaction_name}</td>
                      <td>{transaction.transactionDate}</td>
                      <td>
                        <span className={`type-badge ${transaction.type}`}>
                          {transaction.type}
                        </span>
                      </td>
                      <td>{transaction.category}</td>
                      <td>{formatCurrency(transaction.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.length > 10 && (
                <p className="preview-more">... and {preview.length - 10} more transactions</p>
              )}
            </div>

            {isUploading ? (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  ></div>
                </div>
                <p>Uploading {uploadProgress.current} of {uploadProgress.total} transactions...</p>
              </div>
            ) : (
              <button className="upload-button" onClick={handleUpload}>
                Upload {preview.length} Transaction(s)
              </button>
            )}
          </div>
        )}

        {uploadResults && (
          <div className="upload-results">
            <h3>Upload Results</h3>
            <div className="results-summary">
              <div className="result-item success">
                <span className="result-label">Successful:</span>
                <span className="result-value">{uploadResults.success}</span>
              </div>
              <div className="result-item failed">
                <span className="result-label">Failed:</span>
                <span className="result-value">{uploadResults.failed}</span>
              </div>
            </div>
            {uploadResults.errors.length > 0 && (
              <div className="error-details">
                <h4>Errors:</h4>
                <ul>
                  {uploadResults.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CSVUpload;

